import {
  useState,
  useContext,
  createContext,
  forwardRef,
  ForwardedRef,
  PropsWithChildren,
  SetStateAction,
  Dispatch
} from "react";

import * as _ from "lodash";
import useDeepCompareEffect from "use-deep-compare-effect";
import { Options } from "@ant-design/charts";

import {
  ChartData,
  DataType,
  MetaType,
  ExtendedEventList,
  ExtendedEventEntity,
  ChartViewOptions,
  ChartDataOptions,
  ChartEntity,
  EventSourceType,
  DataDescriptorEntity
} from "@ctra/api";

import { SeriesType } from "../../typings";
import { Optional, TS } from "@ctra/utils";
import { useDataDictionary } from "@ctra/enterprise/src/modules/base";

type ChartProviderProps = {
  entities: Array<ChartEntity>;
  data: Record<ChartEntity["id"], Optional<ChartData["data"]>>;
  meta: Record<ChartEntity["id"], ChartData["meta"]>;
  viewOptions?: ChartViewOptions;
  dataOptions?: ChartDataOptions;
  config?: Partial<Options>;
  series?: SeriesType;
  events?: ExtendedEventList;
};

type ContextType<T extends ChartData = ChartData> = {
  ref: ForwardedRef<any>;
  values: {
    data: Record<ChartEntity["id"], Optional<T["data"]>>;
    meta: Record<ChartEntity["id"], Optional<T["meta"]>>;
    variants: Record<ChartEntity["id"], DataDescriptorEntity>;
    entities: Array<ChartEntity>;
    series: SeriesType;
    viewOptions: ChartViewOptions;
    dataOptions: ChartDataOptions;
    config: Partial<Options>;
    events: Record<string, Array<ExtendedEventEntity>>;
  };
  consolidatedValues: {
    data: T["data"];
    series: SeriesType;
  };
  api: {
    setContextConfig: Dispatch<SetStateAction<Partial<Options>>>;
    setConsolidatedData: (data: T["data"]) => void;
    setConsolidatedSeries: (series: SeriesType) => void;
    toggleEvents: (on: Record<EventSourceType, boolean>) => void;
    toggleAnnotations: (on: boolean) => void;
    toggleAnomalies: (on: boolean) => void;
  };
};

export type ChartContextType<T extends ChartData> = ContextType<T>;

/**
 * Default custom config
 */
const defaultConfig: Partial<Options> = {
  // legend: { position: "top" }
  // height: 220
};

/**
 * Default view options
 * @type {{zoomEnabled: boolean, zoomed: boolean, height: number}}
 */
const defaultViewOptions: ChartViewOptions = {
  zoomed: false,
  zoomEnabled: true
};

const DefaultContext = createContext<ContextType<ChartData<DataType, MetaType>>>({
  ref: null,
  values: {
    data: {},
    meta: {},
    variants: {},
    entities: [],
    series: {},
    dataOptions: {},
    viewOptions: defaultViewOptions,
    config: defaultConfig,
    events: {}
  },
  consolidatedValues: {
    data: [],
    series: {}
  },
  api: {
    setContextConfig: _.noop,
    setConsolidatedData: _.noop,
    setConsolidatedSeries: _.noop,
    toggleEvents: _.noop,
    toggleAnnotations: _.noop,
    toggleAnomalies: _.noop
  }
});

/**
 * Group events
 * @param {ExtendedEventList} events
 * @return {Dictionary<[unknown, ...unknown[]]>}
 */
const groupEvents = (events: ExtendedEventList) =>
  _.groupBy(events, (event) => TS.asMoment(event.startAt).format("YYYY-MM-DD"));

/**
 * @note Instead of filtering first, we are sending all the info down so that different charts can compose what they need.
 * So, in summary: Context -> (Filter + Config + Data + Chart) as against Context -> (Filter + Config + Data) -> Chart
 * @param {ChartEntity} entity
 * @param {Array<{x: number, y: string, seriesField: string}> | Array<{x: string, y: number, seriesField: string}> | Array<{x: string, y: Nullable<number>, seriesField: string, anomaly?: {prediction?: number, type?: string}}> | Array<{x: string | number, y: string, z: number}> | Array<Record<string, string | number | string[] | null | undefined>>} data
 * @param {(DataPointsMetaSource & TranslatedMeta) | TableMeta} meta
 * @param {any[]} series
 * @param {{}} events
 * @param {{}} viewOptions
 * @param {{}} dataOptions
 * @param {{}} config
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} children
 * @returns {JSX.Element}
 */
const _ChartContextProvider = forwardRef<unknown, PropsWithChildren<ChartProviderProps>>(
  (
    {
      entities,
      data,
      meta,
      series = {},
      events = {},
      viewOptions = {},
      dataOptions = {},
      config = {},
      children
    },
    ref
  ) => {
    const { dataDescriptors } = useDataDictionary();

    const [contextEntities, setContextEntities] = useState<Array<ChartEntity>>(entities);
    const [contextData, setContextData] = useState<ChartProviderProps["data"]>(data);
    const [contextMeta, setContextMeta] = useState<ChartProviderProps["meta"]>(meta);
    const [contextConfig, setContextConfig] = useState<Partial<Options>>(_.merge({}, defaultConfig, config));
    const [contextDataOptions, setContextDataOptions] = useState(dataOptions);
    const [contextVariants, setContextVariants] = useState<Record<ChartEntity["id"], DataDescriptorEntity>>(
      {}
    );

    const [contextViewOptions, setContextViewOptions] = useState<ChartViewOptions>({
      ...defaultViewOptions,
      ...viewOptions
    });

    const [contextEvents, setContextEvents] = useState(groupEvents(events));
    const [contextSeries, setContextSeries] = useState(series);

    useDeepCompareEffect(() => {
      setContextEntities(entities);
    }, [entities]);

    useDeepCompareEffect(() => {
      setContextConfig((previousConfig) => _.merge({}, previousConfig, config));
    }, [config]);

    useDeepCompareEffect(() => {
      setContextViewOptions((previousViewOptions) => ({ ...previousViewOptions, ...viewOptions }));
    }, [viewOptions]);

    useDeepCompareEffect(() => {
      setContextDataOptions((previousDataOptions) => _.merge({}, previousDataOptions, dataOptions));
    }, [dataOptions]);

    useDeepCompareEffect(() => {
      setContextData(data);
    }, [data]);

    useDeepCompareEffect(() => {
      setContextEvents(groupEvents(events));
    }, [events]);

    useDeepCompareEffect(() => {
      const variantIDCollection = _.mapValues(meta, "variantId");

      setContextMeta(meta);

      setContextVariants(
        _.mapValues(meta, (v, chartID) => {
          const variantID = variantIDCollection[chartID];

          return _.get(dataDescriptors, [variantID]);
        })
      );
    }, [meta]);

    useDeepCompareEffect(() => {
      setContextSeries(series);
    }, [series]);

    /**
     * Consolidated data for chart rendering
     */
    const [consolidatedData, setConsolidatedData] = useState<ChartData["data"]>([]);

    /**
     * Consolidated series for chart rendering
     */
    const [consolidatedSeries, setConsolidatedSeries] = useState<SeriesType>(series);

    /**
     * Context values
     * @type {{variants: Record<string, DataDescriptorEntity>, entities: Array<ChartEntity>, series: {}, config: Partial<Options>, data: Record<string, Array<{x: number, y: string, seriesField: string, context?: PointContext}> | Array<{x: string, y: number, seriesField: string, groupField?: string, context?: PointContext}> | Array<{x: string, y: Nullable<number>, seriesField: string, context?: PointContext, isolated?: "both" | "left" | "right", missing?: boolean, anomaly?: {prediction?: number, type?: string}}> | Array<{x: string | number, y: string, z: number, context?: PointContext}> | Array<Record<string, string | number | string[] | null | undefined>> | undefined>, meta: Record<string, (DataPointsMetaSource & TranslatedMeta) | TableMeta>, viewOptions: ChartViewOptions, dataOptions: {}, events: Dictionary<[ExtendedEventList[keyof ExtendedEventList], ...Array<ExtendedEventList[keyof ExtendedEventList]>]>}}
     */
    const values = {
      variants: contextVariants,
      entities: contextEntities,
      series: contextSeries,
      config: contextConfig,
      data: contextData,
      meta: contextMeta,
      viewOptions: contextViewOptions,
      dataOptions: contextDataOptions,
      events: contextEvents
    };

    /**
     * Set the context values on the ref to make it available to the parent component
     */
    if (!!ref && _.has(ref, "current")) {
      _.set(ref, "current.contextValues", values);
    }

    return (
      <DefaultContext.Provider
        value={{
          ref,
          values,
          consolidatedValues: {
            data: consolidatedData,
            series: consolidatedSeries
          },
          api: {
            setContextConfig,
            setConsolidatedData,
            setConsolidatedSeries,
            toggleEvents: (on) => {
              setContextViewOptions((viewOptions) => ({ ...viewOptions, showEvents: on }));
            },
            toggleAnnotations: (on) => {
              setContextViewOptions((viewOptions) => ({ ...viewOptions, showAnnotations: on }));
            },
            toggleAnomalies: (on) => {
              setContextViewOptions((viewOptions) => ({ ...viewOptions, showAnomalies: on }));
            }
          }
        }}
      >
        {children}
      </DefaultContext.Provider>
    );
  }
);

export const ChartContext = {
  Provider: _ChartContextProvider,
  Consumer: DefaultContext.Consumer
};

/**
 * Hook for chart data, series and config provider context
 * @returns
 */
export const useChartContext = <T extends ChartData>(): ContextType<T> =>
  useContext(DefaultContext) as unknown as ContextType<T>;
